{
  "$schema": "https://json-schema.org/draft/2020-12/schema",
  "title": "PainterStrokesFile",
  "description": "The ditch strokes Cornelis Anthonisz. drew over each polder the painter view shows, measured on docs/reference/cornelis-anthonisz-1538.jpg and turned into the layout-plane angle and spacing at which the painter view draws them, keyed by layout polder id. Walk and fly views keep the surveyed ditches; the painter view fades these in with its amount. A polder with fewer than minTiles agreeing tiles keeps its surveyed ditches and has no painter member. Written by tools/painterStrokes (node tools/painterStrokes/run.mjs) from this painting, src/data/painterView.json and the layout; re-run it whenever any of them changes.",
  "type": "object",
  "additionalProperties": false,
  "required": ["version", "source", "method", "polders"],
  "properties": {
    "version": { "description": "Schema version.", "const": 1 },
    "source": {
      "title": "PainterStrokesSource",
      "type": "object",
      "additionalProperties": false,
      "required": ["image", "width", "height"],
      "properties": {
        "image": { "type": "string", "description": "Project-relative path of the painting." },
        "width": { "type": "integer", "description": "Pixels." },
        "height": { "type": "integer", "description": "Pixels." }
      }
    },
    "method": {
      "title": "PainterStrokesMethod",
      "type": "object",
      "additionalProperties": false,
      "required": [
        "topHatPx",
        "fieldBlurSigmaPx",
        "fieldClasses",
        "labelStepPx",
        "erodePx",
        "tilePx",
        "tileStridePx",
        "minTileCover",
        "minFit",
        "minSpacingPx",
        "minPeriodicity",
        "agreeDeg",
        "minTiles"
      ],
      "properties": {
        "topHatPx": {
          "type": "integer",
          "minimum": 3,
          "description": "Side of the square black top-hat of L* and of b* whose sum marks the painted ditch lines, darker and bluer than the field they cross, pixels."
        },
        "fieldBlurSigmaPx": {
          "type": "number",
          "exclusiveMinimum": 0,
          "description": "Standard deviation of the blur under which a pixel counts as field where its colour is nearest a field class of the palette, pixels."
        },
        "fieldClasses": {
          "type": "array",
          "items": { "type": "string", "minLength": 1 },
          "minItems": 1,
          "description": "Palette classes that count as field."
        },
        "labelStepPx": {
          "type": "integer",
          "minimum": 1,
          "description": "Every labelStepPx-th pixel in each direction is traced to the polder it shows; the pixels between take its polder."
        },
        "erodePx": {
          "type": "integer",
          "minimum": 0,
          "description": "Pixels taken off each polder's edge, where its dikes, roads and banks lie."
        },
        "tilePx": {
          "type": "integer",
          "minimum": 16,
          "description": "Side of a square tile, pixels."
        },
        "tileStridePx": {
          "type": "integer",
          "minimum": 1,
          "description": "Step between tiles, pixels."
        },
        "minTileCover": {
          "type": "number",
          "exclusiveMinimum": 0,
          "maximum": 1,
          "description": "Share of a tile that must be the polder's field for the tile to be measured."
        },
        "minFit": {
          "type": "number",
          "minimum": 0,
          "maximum": 1,
          "description": "Least share of the tile's line response a profile across the best angle explains for the tile to resolve."
        },
        "minSpacingPx": {
          "type": "number",
          "exclusiveMinimum": 0,
          "description": "Closest line spacing a tile can resolve, pixels."
        },
        "minPeriodicity": {
          "type": "number",
          "minimum": 0,
          "maximum": 1,
          "description": "Least normalised autocorrelation of the profile at the line spacing for the tile to resolve."
        },
        "agreeDeg": {
          "type": "number",
          "exclusiveMinimum": 0,
          "description": "Half-width of the angle window about the polder's dominant stroke angle in which resolved tiles agree, degrees."
        },
        "minTiles": {
          "type": "integer",
          "minimum": 1,
          "description": "Fewest agreeing tiles for a polder to take the painted strokes."
        }
      }
    },
    "polders": {
      "type": "object",
      "description": "Every layout polder with at least one measured tile, keyed by layout id, in layout order.",
      "additionalProperties": { "$ref": "#/$defs/PolderStrokes" }
    }
  },
  "$defs": {
    "Vec2": {
      "title": "Vec2",
      "description": "Point [x east, y north] in metres.",
      "type": "array",
      "items": { "type": "number" },
      "minItems": 2,
      "maxItems": 2
    },
    "Ditches": {
      "title": "Ditches",
      "type": "object",
      "additionalProperties": false,
      "required": ["ditchAngle", "ditchSpacing"],
      "properties": {
        "ditchAngle": {
          "type": "number",
          "minimum": 0,
          "exclusiveMaximum": 3.1416,
          "description": "Direction the ditches run in, radians counter-clockwise from east with y north, in [0, pi)."
        },
        "ditchSpacing": {
          "type": "number",
          "exclusiveMinimum": 0,
          "description": "Distance between neighbouring ditches, metres."
        }
      }
    },
    "PaintedLines": {
      "title": "PaintedLines",
      "type": "object",
      "additionalProperties": false,
      "required": ["angleDeg", "spacingPx"],
      "properties": {
        "angleDeg": {
          "type": "number",
          "minimum": 0,
          "exclusiveMaximum": 180,
          "description": "Median direction of the painted lines over the agreeing tiles, degrees counter-clockwise from the image x axis with y up."
        },
        "spacingPx": {
          "type": "number",
          "exclusiveMinimum": 0,
          "description": "Median spacing of the painted lines over the agreeing tiles, pixels of the painting."
        }
      }
    },
    "PolderStrokes": {
      "title": "PolderStrokes",
      "type": "object",
      "additionalProperties": false,
      "required": ["survey", "tiles", "resolved", "agreeing"],
      "properties": {
        "survey": { "$ref": "#/$defs/Ditches" },
        "tiles": {
          "type": "integer",
          "minimum": 1,
          "description": "Tiles with at least minTileCover of the polder's field."
        },
        "resolved": {
          "type": "integer",
          "minimum": 0,
          "description": "Tiles whose lines resolved: fit and periodicity at or above their minima."
        },
        "agreeing": {
          "type": "integer",
          "minimum": 0,
          "description": "Resolved tiles within agreeDeg of the dominant stroke angle."
        },
        "painting": { "$ref": "#/$defs/PaintedLines" },
        "painter": { "$ref": "#/$defs/Ditches" },
        "polygon": {
          "type": "array",
          "minItems": 3,
          "items": { "$ref": "#/$defs/Vec2" },
          "description": "The polder's layout polygon, which the painter view looks its fragments up in; present with painter."
        }
      }
    }
  }
}
